import { UIKit } from "@/shared/ui";
/**
 * Виджет «Свойства» — правая панель редактора.
 *
 * Показывает параметры выделенного объекта (стена / проём / угол / прибор / светильник /
 * кабель) и позволяет задать их числом, а не мышью: для чертежа это основной способ ввода.
 * У выключателя здесь видно, какие светильники он зажигает, у светильника — какой
 * выключатель его включает: группа освещения читается из проводки, а не хранится.
 * Не хранит паспорт проекта и экспликацию: это отдельное меню верхней панели.
 */
import { MaterialSwatch, WallMaterialPicker } from "@/widgets/catalog";
import { type CatalogItem } from "@/shared/catalog";
import { useWallsStore } from "@/shared/store/layers";
import { SelectionKind, ViewMode, useEditorStore } from "@/widgets/editor";
import { DEFAULT_WALL_MATERIAL, useProjectStore } from "@/shared/store";
import { getMaterial } from "@/shared/catalog";
import { ObjectComments } from "./ObjectComments";
import { WallSizeFields } from "./WallSizeFields";

export type PropertiesPanelCatalogItem = CatalogItem;

export const WallProperties = () => {
	const layers = useProjectStore((s) => s.project.layers);
	const graph = layers.walls;
	const selection = useEditorStore((s) => s.selection);
	const viewMode = useEditorStore((s) => s.viewMode);
	const updateWall = useWallsStore((s) => s.updateWall);
	const pushHistory = useProjectStore((s) => s.pushHistory);

	// Хелпер для updateFixture в зависимости от каталога

	if (!selection) return null;
	if (selection.kind === SelectionKind.Wall) {
		const wall = graph.walls[selection.id];
		if (!wall) return null;
		return (
			<>
				<UIKit.Text className="text-sm font-semibold tracking-label text-ink uppercase">
					Стена
				</UIKit.Text>
				<WallSizeFields wallId={wall.id} />

				{/* Несущая — роль стены, а не материал: переключатель отдельно от списка материалов */}
				<UIKit.Button
					type="button"
					role="switch"
					aria-checked={wall.bearing ?? false}
					className={`flex items-center gap-2.25 rounded-md border px-2 py-1.5 text-left text-xs${
						wall.bearing
							? " border-danger-border bg-danger-soft font-medium text-danger"
							: " border-border bg-surface text-ink hover:border-slate-300"
					}`}
					onClick={() => updateWall(wall.id, { bearing: !wall.bearing })}
				>
					<MaterialSwatch material={getMaterial(wall.material)} bearing />
					<UIKit.Text as="span" className="flex-1">
						Несущая стена
					</UIKit.Text>
					<UIKit.Text as="span" className={`text-xs${wall.bearing ? " text-danger" : " text-ink"}`}>
						{wall.bearing ? "да" : "нет"}
					</UIKit.Text>
				</UIKit.Button>

				{/* Статус этапа стены: исходный план, демонтаж или монтаж */}
				{viewMode !== ViewMode.ThreeD && (
					<div className="flex flex-col gap-1">
						<UIKit.Text
							as="span"
							className="mt-1 text-xs font-semibold tracking-label text-ink uppercase"
						>
							Этап ремонта
						</UIKit.Text>
						<div className="grid grid-cols-3 gap-1">
							<UIKit.Button
								type="button"
								className={`px-2 py-1.5 text-center text-xs ${
									!wall.demolished && !wall.mounted
										? "border-accent bg-accent-soft font-medium text-accent"
										: "text-ink"
								}`}
								onClick={() => {
									pushHistory();
									updateWall(wall.id, { demolished: false, mounted: false });
								}}
							>
								Исходный
							</UIKit.Button>
							<UIKit.Button
								type="button"
								className={`px-2 py-1.5 text-center text-xs ${
									wall.demolished
										? "border-danger-border bg-danger-soft font-medium text-danger"
										: "text-ink"
								}`}
								onClick={() => {
									pushHistory();
									updateWall(wall.id, { demolished: !wall.demolished, mounted: false });
								}}
							>
								Демонтаж
							</UIKit.Button>
							<UIKit.Button
								type="button"
								className={`px-2 py-1.5 text-center text-xs ${
									wall.mounted && !wall.demolished
										? "border-emerald-500 bg-emerald-50 font-medium text-emerald-700"
										: "text-ink"
								}`}
								onClick={() => {
									pushHistory();
									updateWall(wall.id, { mounted: !wall.mounted, demolished: false });
								}}
							>
								Монтаж
							</UIKit.Button>
						</div>
					</div>
				)}

				{/* Материал — конструкция стены: он виден штриховкой на чертеже, а в 3D
				    стену закрывает отделка. Поэтому выбор материала — только в 2D */}
				{viewMode !== ViewMode.ThreeD && (
					<>
						<UIKit.Text
							as="span"
							className="mt-1 text-xs font-semibold tracking-label text-ink uppercase"
						>
							Материал
						</UIKit.Text>
						<WallMaterialPicker
							value={wall.material ?? DEFAULT_WALL_MATERIAL}
							bearing={wall.bearing}
							onPick={(material) => updateWall(wall.id, { material: material.material })}
						/>
					</>
				)}

				<ObjectComments kind="wall" id={wall.id} />
			</>
		);
	}

	return null;
};

/** Список типов раздела каталога — сменить тип объекта, не переставляя его. */
export function CatalogTypeList({
	items,
	current,
	onPick,
}: {
	items: CatalogItem[];
	current: string;
	onPick: (itemId: string) => void;
}) {
	if (items.length === 0) return null;
	return (
		<>
			<UIKit.Text
				as="span"
				className="mt-1 text-xs font-semibold tracking-label text-ink uppercase"
			>
				Тип
			</UIKit.Text>
			<div className="flex flex-col gap-1">
				{items.map((item) => (
					<UIKit.Button
						key={item.id}
						type="button"
						className={`flex items-center gap-2 rounded-lg border border-transparent px-2.25 py-1.75 text-left text-sm text-ink hover:bg-[#f3f5f9]${
							current === item.id ? " border-accent bg-accent-soft text-accent" : ""
						}`}
						onClick={() => onPick(item.id)}
					>
						<UIKit.Text
							as="span"
							className="size-2.5 shrink-0 rounded-sm"
							style={{ background: item.color }}
						/>
						<UIKit.Text as="span" className="min-w-0 flex-1">
							{item.name}
						</UIKit.Text>
					</UIKit.Button>
				))}
			</div>
		</>
	);
}
